import { Component, Input, OnInit, OnDestroy, SimpleChanges } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PiiDecryptService } from 'src/app/_services/pii-decrypt.service';
import { PiiAuditService, PiiAuditRequest } from 'src/app/_services/pii-audit.service';
import { PiiFormService } from 'src/app/_services/pii-form.service';
import { PiiUnhideModalComponent } from '../pii-unhide-modal/pii-unhide-modal.component';
import { AlertService } from 'src/app/_services/alert.service';
import { StorageService } from 'src/app/_services/storage.service';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-pii-image-field',
  templateUrl: './pii-image-field.component.html',
  styleUrls: ['./pii-image-field.component.scss']
})
export class PiiImageFieldComponent implements OnInit, OnDestroy {
  @Input() encryptedPdfUrl: string = '';
    @Input() reportTitle: string = 'PII Report';
    @Input() reportType: string = 'KYC'; // ICP, EFR, etc.
    @Input() userId: string = '';
    @Input() fieldName: string = '';
    @Input() readonly: boolean = false;
    @Input() entityType: string = 'customer';
    @Input() entityMobile: string = '';
    @Input() referenceId: string = '';
    @Input() useIndividualUnmask: boolean = true;
    @Input() showLabel: boolean = true;
    @Input() buttonText: string = 'View Report';
    @Input() downloadText: string = 'Download Report';
    @Input() downloadable : boolean = false;
  
    isUnmasked = false;
    isIndividuallyUnmasked = false;
    decryptedUrl: string = '';
    safePdfUrl: SafeResourceUrl | null = null;
    isLoading = false;
  
    private unmaskSubscription: Subscription;
  
    constructor(
      private modalService: NgbModal,
      private piiDecryptService: PiiDecryptService,
      private piiAuditService: PiiAuditService,
      private piiFormService: PiiFormService,
      private alertService: AlertService,
      private storageService: StorageService,
      private router: Router,
      private sanitizer: DomSanitizer
    ) {
      // Subscribe to form-level unmask if not using individual unmask
      this.unmaskSubscription = this.piiFormService.formUnmasked$.subscribe((isUnmasked) => {
        if (!this.useIndividualUnmask) {
          this.isUnmasked = isUnmasked;
          if (isUnmasked) {
            this.preparePdfUrl();
          }
        }
      });
    }
  
    ngOnInit(): void {
      this.decryptPdfUrl();
    }
  
    ngOnDestroy(): void {
      if (this.unmaskSubscription) {
        this.unmaskSubscription.unsubscribe();
      }
    }

    ngOnChanges(changes: SimpleChanges): void {
      if (changes['encryptedPdfUrl'] && changes['encryptedPdfUrl'].currentValue) {
        this.decryptPdfUrl();
      }
    }
  
    private decryptPdfUrl(): void {
      if (this.encryptedPdfUrl && this.piiDecryptService.isEncrypted(this.encryptedPdfUrl)) {
        const decrypted = this.piiDecryptService.decrypt(this.encryptedPdfUrl);
        if (decrypted && decrypted !== '[DECRYPTION ERROR]') {
          this.decryptedUrl = decrypted;
        } else {
          this.decryptedUrl = '';
        }
      } else {
        // Not encrypted (legacy case)
        this.decryptedUrl = this.encryptedPdfUrl || '';
      }
    }
  
    private preparePdfUrl(): void {
      // ng2-pdf-viewer handles CORS better than iframe, so we don't need complex logic
      // if (this.decryptedUrl) {
      //   this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.decryptedUrl);
      // }
    }
    
  
    onViewReport(): void {
      if (this.readonly) {
        return;
      }
      
      if (this.useIndividualUnmask) {
        // Individual unmask with audit logging
        this.processIndividualUnmask();
      } else {
        // Form-level unmask (unmasks all fields)
        this.piiFormService.unmaskAllFields(this.userId || 'unknown', this.getEffectiveEntityType());
      }
    }
    
    private processIndividualUnmask(): void {
      // Open the modal to get the reason for unmasking
      const modalRef = this.modalService.open(PiiUnhideModalComponent, {
        backdrop: 'static',
        keyboard: false,
        size: 'lg'
      });
  
      // Pass relevant data to the modal
      modalRef.componentInstance.fieldName = this.reportTitle || this.fieldName + ' Report';
      modalRef.componentInstance.userId = this.userId;
      modalRef.componentInstance.context = 'report';
  
      // Handle modal result
      modalRef.result.then((modalResult) => {
        if (modalResult) {
          // User provided reason and confirmed - proceed with unmasking
          this.performUnmaskWithAudit(modalResult);
        }
      }).catch(() => {
        // Modal dismissed - do nothing
        this.isLoading = false;
      });
    }
    
    private performUnmaskWithAudit(modalResult: any): void {
      this.isLoading = true;
      
      const currentUser = this.storageService.getUserName();
      const auditRequest: PiiAuditRequest = {
        userId: this.userId || 'unknown',
        fieldName: this.fieldName + '_report',
        reasonId: modalResult.reasonId,
        reasonDescription: modalResult.reason || 'View PII Report',
        comment: modalResult.comment || '',
        accessedBy: currentUser || 'unknown',
        timestamp: new Date().toISOString(),
        context: 'report',
        entityType: this.getEffectiveEntityType(),
        entityMobile: this.entityMobile,
        transactionId: '',
        referenceId: this.getReferenceId()
      };
      
      // Log the audit access
      this.piiAuditService.logPiiAccess(auditRequest).subscribe({
        next: (response) => {
          this.isIndividuallyUnmasked = true;
          this.isUnmasked = true;
          this.isLoading = false;
          this.preparePdfUrl();
          this.alertService.successAlertWithMessage('PII report unmasked successfully');
          
          // Auto re-mask after 2 minutes for security
          setTimeout(() => {
            this.onHide();
            this.alertService.toastWarringMessageAlert({
              title: 'Report automatically masked for security'
            });
          }, 120000);
        },
        error: (error) => {
          // Even if audit logging fails, allow unmask for demo
          this.isIndividuallyUnmasked = true;
          this.isUnmasked = true;
          this.isLoading = false;
          this.preparePdfUrl();
          this.alertService.successAlertWithMessage('PII report unmasked successfully');
          
          // Auto re-mask after 2 minutes
          setTimeout(() => {
            this.onHide();
            this.alertService.toastWarringMessageAlert({
              title: 'Report automatically masked for security'
            });
          }, 120000);
        }
      });
    }
  
    onHide(): void {
      if (this.useIndividualUnmask) {
        this.isIndividuallyUnmasked = false;
        this.isUnmasked = false;
        this.safePdfUrl = null;
      } else {
        this.piiFormService.maskAllFields();
      }
    }
  
    onDownload(): void {
      if (this.decryptedUrl && this.isUnmasked) {
        const fileName = `${this.reportType}_Report_${this.userId}_${Date.now()}.jpg`;
        
        // Use fetch to download the Image as blob, then trigger download
        fetch(this.decryptedUrl)
          .then(response => response.blob())
          .then(blob => {
            // Create blob URL
            const blobUrl = window.URL.createObjectURL(blob);
            
            // Create download link
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            
            // Trigger download
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Clean up blob URL
            window.URL.revokeObjectURL(blobUrl);
            
            this.alertService.toastSuccessMessageAlert({
              title: 'Image downloaded successfully'
            });
            
            // Log the download action for audit purposes
            console.log(`PII Image downloaded: ${this.reportType} by user ${this.storageService.getUserName()}`);
          })
          .catch(error => {
            console.error('Image download failed:', error);
            this.alertService.toastErrorMessageAlert({
              title: 'Download failed. Please try again.'
            });
          });
      }
    }
  
    openInNewWindow(): void {
      if (this.decryptedUrl && this.isUnmasked) {
        // Open PDF in new window/tab
        const newWindow = window.open(this.decryptedUrl, '_blank');
        if (!newWindow) {
          // Popup blocked, show download option
          this.alertService.toastWarringMessageAlert({
            title: 'Popup blocked. Please allow popups or use download option.'
          });
        }
      }
    }
    
    // PDF viewer event handlers
    onPdfLoadComplete(event: any): void {
      console.log('Image loaded successfully:', event);
      this.isLoading = false;
    }
    
    onPdfError(event: any): void {
      console.error('Image load error:', event);
      this.alertService.toastWarringMessageAlert({
        title: 'Failed to load Image. Please try downloading the document.'
      });
      this.isLoading = false;
    }
  
    get canViewReport(): boolean {
      return !this.readonly && !this.isUnmasked && !!this.encryptedPdfUrl;
    }
  
    get canDownload(): boolean {
      return this.isUnmasked && !!this.decryptedUrl;
    }
    
    private getEffectiveEntityType(): string {
      if (this.entityType && this.entityType !== 'customer') {
        return this.entityType;
      }
      
      const currentRoute = this.router.url.toLowerCase();
      
      if (currentRoute.includes('transaction')) {
        return 'transaction';
      } else if (currentRoute.includes('merchant')) {
        return 'merchant';
      } else if (currentRoute.includes('reseller')) {
        return 'reseller';
      } else if (currentRoute.includes('customer')) {
        return 'customer';
      }
      
      return this.entityType || 'customer';
    }
    
    private getReferenceId(): string {
      if (this.referenceId) {
        return this.referenceId;
      }
      return this.userId || 'unknown';
    }
}
